import React from 'react'
import { connect } from 'react-redux'
import {
  branch,
  compose,
  lifecycle,
  renderComponent,
  withHandlers,
  withProps,
} from 'recompose'
import { getWaiter } from 'redux-waiter'

// project
import LoadingView from 'components/LoadingView'
import { getLDClient, initializeLDClient } from 'helpers/launchDarklyClient'
import sortByKeys from 'helpers/sort-by-keys'
import { setViewAndShowModal } from 'modules/app-core/modal/actions'
import { actions, constants, selectors } from 'modules/application-engine'
import MessageListContainer from 'modules/employee-messages/containers/MessageListContainer'
import NotesContainer from 'modules/notes/containers/NotesContainer'
import PostContainer from 'modules/notes/containers/PostContainer'

// module
import dayjs from 'dayjs'
import { ApplicationInfo } from 'types/application'
import { DEFAULT_BENEFIT_EFFECTIVE_DATE } from '../../../../modules/application-engine/constants'
import ApplicationsList from './ApplicationsList'
import { handlePopupMessageListener } from 'helpers/pageMessage'

const sortByKeysAscending = (keyString) => sortByKeys(keyString)
const sortByKeysDescending = (keyString) => sortByKeys(keyString, false)

declare global {
  interface Window {
    getAllApplications?: () => void
    updateLocalApplication?: (application: any) => void
    displayLoading?: boolean
  }
}

const sortItems = {
  name: {
    ascending: sortByKeysAscending('CompanyName'),
    descending: sortByKeysDescending('CompanyName'),
  },
  employeeId: {
    ascending: sortByKeysAscending('CompanyEmployeeId'),
    descending: sortByKeysDescending('CompanyEmployeeId'),
  },
  benefitEffectiveDate: {
    ascending: sortByKeysAscending('BenefitEffectiveDate'),
    descending: sortByKeysDescending('BenefitEffectiveDate'),
  },
  carrier: {
    ascending: sortByKeysAscending('CarrierName'),
    descending: sortByKeysDescending('CarrierName'),
  },
  cartCommitted: {
    ascending: sortByKeysAscending('CarrierApplication.WorkerSubmittedDate'),
    descending: sortByKeysDescending('CarrierApplication.WorkerSubmittedDate'),
  },
  hixmeSubmitted: {
    ascending: sortByKeysAscending('CarrierApplication.HixmeSubmittedDate'),
    descending: sortByKeysDescending('CarrierApplication.HixmeSubmittedDate'),
  },
  memberId: {
    ascending: sortByKeysAscending('PolicyNumber'),
    descending: sortByKeysDescending('PolicyNumber'),
  },
  appState: {
    ascending: sortByKeysAscending('CarrierApplication.Status'),
    descending: sortByKeysDescending('CarrierApplication.Status'),
  },
  renewal: {
    ascending: (a: ApplicationInfo, b: ApplicationInfo) => {
      return (
        Number(a.RenewalInformation?.isRenewal ?? false) -
        Number(b.RenewalInformation?.isRenewal ?? false)
      )
    },
    descending: (a: ApplicationInfo, b: ApplicationInfo) => {
      return (
        Number(b.RenewalInformation?.isRenewal ?? false) -
        Number(a.RenewalInformation?.isRenewal ?? false)
      )
    },
  },
  commissionPartner: {
    ascending: (a: ApplicationInfo, b: ApplicationInfo) => {
      const aCP = a.CommissionPartner ?? ''
      const bCP = b.CommissionPartner ?? ''

      return aCP.localeCompare(bCP)
    },
    descending: (a: ApplicationInfo, b: ApplicationInfo) => {
      const aCP = a.CommissionPartner ?? ''
      const bCP = b.CommissionPartner ?? ''

      return bCP.localeCompare(aCP)
    },
  },
  carrierPlanChange: {
    ascending: (a: ApplicationInfo, b: ApplicationInfo) => {
      const aCP = String(a.CarrierPlanChange ?? '')
      const bCP = String(b.CarrierPlanChange ?? '')

      return aCP.localeCompare(bCP)
    },
    descending: (a: ApplicationInfo, b: ApplicationInfo) => {
      const aCP = String(a.CarrierPlanChange ?? '')
      const bCP = String(b.CarrierPlanChange ?? '')

      return bCP.localeCompare(aCP)
    },
  },
}

const getAppEngineConfig = () => {
  let appEngineConfig = {
    refreshEnabled: false,
    refreshRateMS: 10000,
    defaultSkipCache: false,
    defaultBenefitEffectiveDate: DEFAULT_BENEFIT_EFFECTIVE_DATE,
    filterByBenefitEffectiveDateRange: false,
  }

  try {
    const ldClient = getLDClient()
    appEngineConfig = ldClient.variation('product.admin.appengine.config', {
      refreshEnabled: false,
      refreshRateMS: 10000,
      defaultSkipCache: false,
      filterByBenefitEffectiveDateRange: false,
    })
  } catch (e) {
    console.error('Error initializing LaunchDarkly', e)
  }

  return appEngineConfig
}

const mapStateToProps = (state) => {
  const sortBy = selectors.getSortBy(state)
  const sortDescending = selectors.getSortDescending(state)
  const isViewAllSelected = selectors.isViewAllSelected(state)
  const sortDirection = sortDescending ? 'descending' : 'ascending'
  const filteredList = selectors
    .filteredApplicationsList(state)
    .sort(sortItems[sortBy][sortDirection])
  const ret = {
    allApplications: filteredList,
    applications: isViewAllSelected
      ? filteredList
      : filteredList.slice(0, selectors.recordPointerSelector(state)),
    hasMore: selectors.hasMore(state),
    isLoading: getWaiter(state, constants.GET_ALL_APPLICATIONS).isPending,
    isUpdatingAssignee: getWaiter(state, constants.UPDATE_APPLICATION_ASSIGNEE)
      .isPending,
    displayLoading: true,
    sortBy,
    sortDescending,
    benefitEffectiveDateForQuery:
      selectors.getBenefitEffectiveDateForQuery(state),
    benefitEffectiveDateRangeForQuery:
      selectors.getBenefitEffectiveDateRangeForQuery(state),
    filterByBenefitEffectiveDateRange: state?.filterByBenefitEffectiveDateRange,
    defaultSkipCache: state?.defaultSkipCache,
    selectedApplications: selectors.getSelectedApplications(state),
  }
  return ret
}

export function openApplicationDetails(benefitId: string): void {
  const options = `width=600, height=${screen.height}, top=0, left=0`
  const url = `${window.location.origin}/application-manager/${benefitId}`
  window.open(url, 'appWindow', options)
}

export function openOurPeopDetails(employeePublicKey: string): void {
  const options = `width=1000,height=${window.screen.height},top=0,left=0`
  const url = `${window.location.origin}/employees/${employeePublicKey}/coverage`
  window.open(url, 'appWindow', options)
}

export default compose(
  connect(mapStateToProps, {
    setViewAndShowModal,
    ...actions,
  }),
  withProps((_) => {
    const config = getAppEngineConfig()
    return {
      defaultSkipCache: config?.defaultSkipCache ?? false,
      filterByBenefitEffectiveDateRange:
        config?.filterByBenefitEffectiveDateRange ?? false,
    }
  }),
  withHandlers({
    openDetails: () => openApplicationDetails,
    openNotes: (props) => (personId) => {
      props.setViewAndShowModal(() => (
        <NotesContainer personId={personId} NoteContainer={PostContainer} />
      ))
    },
    openMessages: (props) => (personId) => {
      props.setViewAndShowModal(() => (
        <MessageListContainer personId={personId} />
      ))
    },
  }),
  lifecycle({
    intervalId: null,
    refreshAppList() {
      this.props.getAllApplications({
        SkipCache: this.defaultSkipCache,
        BenefitEffectiveDate: this.props.benefitEffectiveDateForQuery,
        BenefitEffectiveDateRange: this.props.benefitEffectiveDateRangeForQuery,
      })
    },
    async componentDidMount() {
      // Load saved filters from localStorage
      this.props.loadSavedFilters()

      let appEngineConfig = {
        refreshEnabled: false,
        refreshRateMS: 10000,
        defaultSkipCache: false,
        defaultBenefitEffectiveDate: DEFAULT_BENEFIT_EFFECTIVE_DATE,
        filterByBenefitEffectiveDateRange: false,
      }
      try {
        await initializeLDClient()
        appEngineConfig = getAppEngineConfig()
      } catch (e) {
        console.error('Error initializing LaunchDarkly', e)
      }
      window.getAllApplications = this.props.getAllApplications
      window.updateLocalApplication = this.props.updateLocalApplication

      let bed = ''
      const bedRange: string[] = []
      const today = dayjs()
      if (
        today.month() >= 10 &&
        appEngineConfig?.filterByBenefitEffectiveDateRange
      ) {
        bedRange[0] = today.add(1, 'year').startOf('year').format('MM-DD-YYYY')
        bedRange[1] = today
          .add(1, 'year')
          .startOf('year')
          .add(2, 'month')
          .endOf('month')
          .format('MM-DD-YYYY')
        this.props.setBenefitEffectiveDateRangeChangeForQuery(bedRange)
      } else if (appEngineConfig?.filterByBenefitEffectiveDateRange) {
        bedRange[0] = today.startOf('year').format('MM-DD-YYYY')
        bedRange[1] = today
          .startOf('year')
          .add(2, 'month')
          .endOf('month')
          .format('MM-DD-YYYY')
        this.props.setBenefitEffectiveDateRangeChangeForQuery(bedRange)
      } else if (today.month() >= 10) {
        // dayjs month is zero-indexed, so 10 = November
        bed = today.add(1, 'year').startOf('year').format('MM-DD-YYYY')
        this.props.setBenefitEffectiveDateChangeForQuery(bed)
      } else {
        bed = today.startOf('year').format('MM-DD-YYYY')
        this.props.setBenefitEffectiveDateChangeForQuery(bed)
      }

      this.defaultSkipCache = appEngineConfig?.defaultSkipCache ?? false

      // Call getAllApplications on initial load
      this.props.getAllApplications({
        SkipCache: this.defaultSkipCache ?? false,
        ...(!appEngineConfig?.filterByBenefitEffectiveDateRange && {
          BenefitEffectiveDate: bed,
        }),
        ...(appEngineConfig?.filterByBenefitEffectiveDateRange && {
          BenefitEffectiveDateRange: bedRange,
        }),
      })
      if (appEngineConfig?.refreshEnabled) {
        window.displayLoading = false // Hide loading spinner, will interrupt work with constant displaying
        this.intervalId = setInterval(
          this.refreshAppList.bind(this),
          appEngineConfig?.refreshRateMS
        )
      } else {
        window.displayLoading = true
      }

      this.messageHandler = (event: MessageEvent) => {
        handlePopupMessageListener({
          event,
          origin: window.location.origin,
          onRefresh: this.refreshAppList.bind(this),
        })
      }
      window.addEventListener('message', this.messageHandler)
    },
    componentWillUnmount() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
      this.props.resetComponent()
      delete window.getAllApplications
      delete window.updateLocalApplication
      if (this.messageHandler) {
        window.removeEventListener('message', this.messageHandler)
      }
    },
  }),
  // Hide loading screen when auto refresh enabled. Otherwise, working would be difficult...
  branch(
    ({ isLoading }) => isLoading && window.displayLoading,
    renderComponent(LoadingView)
  )
)(ApplicationsList)
